export const SEVERITY = {
  red: { hex: "#EF4444", label: "RED", text: "text-red-400", ring: "border-red-500/50", bg: "bg-red-500/10" },
  yellow: { hex: "#EAB308", label: "YELLOW", text: "text-yellow-400", ring: "border-yellow-500/50", bg: "bg-yellow-500/10" },
  green: { hex: "#06B6D4", label: "OK", text: "text-cyan-400", ring: "border-cyan-500/40", bg: "bg-cyan-500/10" },
  stale: { hex: "#64748B", label: "STALE", text: "text-slate-400", ring: "border-slate-500/50", bg: "bg-slate-500/10" },
  none: { hex: "#64748B", label: "—", text: "text-slate-500", ring: "border-slate-700", bg: "bg-slate-800/40" },
};

export function edgeColor(sev) {
  return (SEVERITY[sev] || SEVERITY.none).hex;
}

export const STATUS_STYLE = {
  New: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10",
  "In-progress": "text-violet-300 border-violet-500/40 bg-violet-500/10",
  Monitoring: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  "On-hold": "text-amber-300 border-amber-500/40 bg-amber-500/10",
  "Pending Disposition": "text-orange-300 border-orange-500/40 bg-orange-500/10",
  Completed: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  "Not Completed": "text-slate-300 border-slate-500/40 bg-slate-500/10",
};

// Friendly "what are we waiting for" stage derived from the raw workflow status.
export function stageOf(r) {
  const st = r?.status;
  if (st === "New")
    return { key: "awaiting_confirm", label: "Awaiting confirmation", hint: "Placed — waiting for the receiving contact to confirm receipt.", icon: "clock", cls: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10" };
  if (st === "In-progress")
    return { key: "awaiting_outcome", label: "Awaiting outcome", hint: "Receipt confirmed — waiting on the admit / not-admitted decision.", icon: "hourglass", cls: "text-violet-300 border-violet-500/40 bg-violet-500/10" };
  if (st === "Monitoring")
    return { key: "monitoring", label: "Monitoring", hint: "Being monitored — a re-check is scheduled.", icon: "eye", cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" };
  if (st === "On-hold")
    return { key: "on_hold", label: "On hold", hint: "Paused — waiting on an external dependency.", icon: "pause", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" };
  if (st === "Pending Disposition") {
    const overdue = r.aging?.followup_overdue ?? r.followup_overdue;
    const fu = r.recheck_date || r.followup_date;
    return {
      key: "pending", label: overdue ? "Pending · follow-up overdue" : "Pending disposition", icon: "calendar",
      hint: `Follow-up needed${r.pending_reason ? ` — ${r.pending_reason}` : ""}${fu ? ` · follow-up ${fmtDate(fu)}` : ""}.`,
      cls: overdue ? "text-red-300 border-red-500/40 bg-red-500/10" : "text-orange-300 border-orange-500/40 bg-orange-500/10",
    };
  }
  if (st === "Completed")
    return { key: "admitted", label: r.completion_type === "Admit" ? "Admitted" : "Completed", hint: "Closed — admitted / start of care recorded.", icon: "check", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" };
  if (st === "Not Completed")
    return { key: "not_admitted", label: r.reason_code ? `Not admitted · ${r.reason_code}` : "Not admitted", hint: "Closed — not admitted.", icon: "x", cls: "text-slate-300 border-slate-500/40 bg-slate-500/10" };
  return { key: "unknown", label: st || "—", hint: "", icon: "clock", cls: "text-slate-300 border-slate-500/40 bg-slate-500/10" };
}

export function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(String(s).slice(0, 10) + "T00:00:00");
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "2-digit" });
}

export function fmtDateTime(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function roleLabel(r) {
  return { admin: "Administrator", coordinator: "Coordinator", manager: "Manager" }[r] || r;
}

// Map dark-tuned accent hexes to darker, WCAG-legible variants for light theme.
const LIGHT_HEX = {
  "#22D3EE": "#0e7490",
  "#06B6D4": "#0e7490",
  "#EF4444": "#dc2626",
  "#EAB308": "#a16207",
  "#F59E0B": "#b45309",
  "#64748B": "#475569",
  "#34D399": "#047857",
  "#8B5CF6": "#6d28d9",
};

export function themedHex(hex, theme) {
  return theme === "light" ? LIGHT_HEX[hex] || hex : hex;
}
